import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, Check, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [variant, setVariant] = useState({});
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/products/${id}`)
      .then((r) => {
        setP(r.data);
        const init = {};
        (r.data.variants || []).forEach((v) => {
          if (v.options?.length) init[v.name] = v.options[0];
        });
        setVariant(init);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-[#9ca3af]">Loading…</div>;
  if (!p) return (
    <div className="max-w-[1400px] mx-auto px-6 py-20">
      <p className="text-[#9ca3af]">Product not found.</p>
      <Link to="/shop" className="btn-ghost mt-6 inline-flex">BACK TO SHOP</Link>
    </div>
  );

  const handleAdd = () => {
    addItem(p, quantity, Object.keys(variant).length ? variant : null);
    toast.success("Added to cart");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="product-detail-page">
      <Link to="/shop" className="text-xs tracking-[0.2em] uppercase text-[#9ca3af] hover:text-white">
        ← Back to shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square overflow-hidden bg-[#121212] border border-white/10">
            {p.images?.[activeImage] && (
              <img
                src={p.images[activeImage]}
                alt={p.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            )}
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`w-20 h-20 overflow-hidden border ${i === activeImage ? "border-[#c8102e]" : "border-white/10"}`}
                >
                  <img src={src} alt={`view-${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <p className="bgc-eyebrow text-[#c8102e]">{p.category}</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight uppercase mt-3">{p.name}</h1>
          {p.tagline && (
            <p className="font-display text-xl tracking-wider uppercase text-white/80 mt-3">{p.tagline}</p>
          )}
          {p.subtitle && (
            <p className="text-[#c8102e] text-sm tracking-[0.2em] uppercase mt-1">{p.subtitle}</p>
          )}
          <p className="text-3xl font-bold mt-4" data-testid="product-price">${p.price.toFixed(2)}</p>
          <p className="text-[#d1d5db] leading-relaxed mt-6">{p.description}</p>

          {/* Variants */}
          {p.variants?.length > 0 && (
            <div className="mt-8 space-y-5">
              {p.variants.map((v) => (
                <div key={v.name}>
                  <p className="bgc-eyebrow mb-3">{v.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {v.options.map((opt) => {
                      const active = variant[v.name] === opt;
                      return (
                        <button
                          key={opt}
                          onClick={() => setVariant({ ...variant, [v.name]: opt })}
                          data-testid={`variant-${v.name}-${opt}`}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            active
                              ? "bg-white text-black border-white"
                              : "border-white/20 text-white hover:border-white"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quantity + Add */}
          <div className="flex items-center gap-4 mt-10">
            <div className="flex items-center border border-white/20">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-white/5" data-testid="qty-dec">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-5 text-base" data-testid="qty-value">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-white/5" data-testid="qty-inc">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button onClick={handleAdd} disabled={!p.in_stock} className="btn-primary flex-1 justify-center disabled:opacity-50" data-testid="add-to-cart-button">
              {p.in_stock ? "ADD TO CART" : "SOLD OUT"}
            </button>
          </div>

          <ul className="mt-10 space-y-3 text-sm text-[#9ca3af] border-t border-white/10 pt-6">
            <li className="flex items-center gap-3"><Check className="w-4 h-4 text-[#c8102e]" /> Hand-finished in the USA</li>
            <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#c8102e]" /> Lifetime hardware warranty</li>
            <li className="flex items-center gap-3"><Truck className="w-4 h-4 text-[#c8102e]" /> Ships within 48 hours</li>
          </ul>
        </div>
      </div>

      {/* ── Detailed spec sections ── */}
      {(p.features?.length > 0 || Object.keys(p.specifications || {}).length > 0) && (
        <section className="mt-24 border-t border-white/10 pt-16" data-testid="product-spec-section">
          {p.features?.length > 0 && (
            <div className="mb-16">
              <div className="bgc-eyebrow text-[#c8102e]">Design Highlights</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase mt-3">Built to stand out.</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                {p.features.map((f, i) => (
                  <div key={i} className="border border-white/10 p-6 hover:border-[#c8102e] transition-colors" data-testid={`feature-${i}`}>
                    <h3 className="font-display text-lg tracking-wider uppercase text-[#c8102e]">{f.title}</h3>
                    <p className="text-[#d1d5db] mt-3 text-sm leading-relaxed">{f.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Specifications */}
            {Object.keys(p.specifications || {}).length > 0 && (
              <div className="border border-white/10 p-8 bg-[#0a0a0a]" data-testid="product-specifications">
                <div className="bgc-eyebrow text-[#c8102e]">Specifications</div>
                <h3 className="font-display text-2xl tracking-wider uppercase mt-2">Built right.</h3>
                <dl className="mt-6 divide-y divide-white/5">
                  {Object.entries(p.specifications).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-3 text-sm">
                      <dt className="text-[#9ca3af] uppercase tracking-[0.15em] text-xs">{k}</dt>
                      <dd className="text-white text-right max-w-[60%]">{v}</dd>
                    </div>
                  ))}
                  {(p.dimensions?.side || p.dimensions?.front_width) && (
                    <div className="flex justify-between py-3 text-sm">
                      <dt className="text-[#9ca3af] uppercase tracking-[0.15em] text-xs">Dimensions</dt>
                      <dd className="text-white text-right">
                        {p.dimensions.side && <>Side: {p.dimensions.side}<br /></>}
                        {p.dimensions.front_width && <>Front: {p.dimensions.front_width} × {p.dimensions.front_height}</>}
                      </dd>
                    </div>
                  )}
                  {p.adjustability && (
                    <div className="flex justify-between py-3 text-sm">
                      <dt className="text-[#9ca3af] uppercase tracking-[0.15em] text-xs">Adjustability</dt>
                      <dd className="text-white text-right max-w-[60%]">{p.adjustability}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Mounting */}
            {p.mounting?.length > 0 && (
              <div className="border border-white/10 p-8 bg-[#0a0a0a]" data-testid="product-mounting">
                <div className="bgc-eyebrow text-[#c8102e]">Mounting Options</div>
                <h3 className="font-display text-2xl tracking-wider uppercase mt-2">Universal fit.</h3>
                <div className="mt-6 space-y-5">
                  {p.mounting.map((m, i) => (
                    <div key={i} className="border-l-2 border-[#c8102e] pl-4">
                      <p className="font-display text-base tracking-wider uppercase">{m.name}</p>
                      <p className="text-sm text-[#d1d5db] mt-1">{m.fit}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#6b7280] mt-8">* Check local regulations for mirror usage.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
